// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  AltoSpinningLoader,
  Body,
  Column,
  InlineAlert,
  InputSelect,
  LgPadding,
} from '@alto/design-system';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link as RRLink } from 'react-router-dom';
// eslint-disable-next-line import/no-deprecated
import { fetchFacilities } from '~shared/actions/facilities';
import rx from '~shared/assets/images/prescription@2x.png';
import { getCompanyPhone } from '~shared/config/getCompanyPhone';
import { DEFAULT_ERROR_ALERT_TITLE, FAX } from '~shared/constants';
import { getIsLoggedIn } from '~shared/features/auth/selectors/getIsAuthorized';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getFacilityOptions from '~shared/selectors/getFacilityOptions';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { Image } from '~web/components/Image';

export const PharmacyInfoActionSheet = () => {
  const dispatch = useDispatchShared();
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { trackEvent, trackPageView } = useAnalytics();
  const [selectedFacilityID, setSelectedFacilityID] = useState<string | undefined>(undefined);
  const isLoggedIn = useSelectorShared(getIsLoggedIn);
  const fetchFacilitiesLoading = useSelectorShared((state) => state.ui.loading.fetchFacilitiesLoading);
  const fetchFacilitiesError = useSelectorShared((state) => state.ui.errors.fetchFacilitiesError);
  const facilityOptions = useSelectorShared(getFacilityOptions);

  useEffect(() => {
    trackPageView({
      event: EVENTS.PHARMACY_INFO_VIEWED,
      params: {
        flow: isLoggedIn ? 'authenticated' : 'unauthenticated',
      },
      enabled: true,
    });
  }, [isLoggedIn, trackPageView]);

  useEffect(() => {
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchFacilities());
  }, [dispatch]);

  const selectFacility = useCallback(
    (facilityID: string) => {
      trackEvent({
        event: EVENTS.PHARMACY_INFO__LOCATION_SELECTED,
        params: {
          selectedFacilityId: Number(facilityID),
        },
      });
      setSelectedFacilityID(facilityID);
    },
    [trackEvent],
  );

  const renderSelectedFacility = useCallback(
    (selectedFacilityID: string | null | undefined) => {
      let selectedFacility;

      if (selectedFacilityID && facilityOptions.length) {
        selectedFacility = facilityOptions.find((facility) => facility.data.id.toString() === selectedFacilityID);
      }

      if (selectedFacility) {
        return (
          <>
            <Column>
              <Body fontFamily="bold">ADDRESS</Body>
              <Body>{selectedFacility.data.full_street_address}</Body>
              <Body>{selectedFacility.data.city_state_zip}</Body>
            </Column>
            <Column>
              <Body fontFamily="bold">CONTACT INFO</Body>
              <Body>{`Phone: ${getCompanyPhone()}`}</Body>
              <Body>{`Fax: ${FAX}`}</Body>
            </Column>
            {selectedFacility.data.npi ? (
              <Column>
                <Body fontFamily="bold">NPI</Body>
                <Body>{selectedFacility.data.npi}</Body>
              </Column>
            ) : null}
          </>
        );
      }

      return null;
    },
    [facilityOptions],
  );

  const showLocations = !fetchFacilitiesLoading && fetchFacilitiesError === null;
  const showLoadingOrError = fetchFacilitiesLoading || !!fetchFacilitiesError;

  return (
    <ActionSheetV2
      title="Pharmacy Info"
      analyticsName="pharmacy_info"
    >
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <Image
          center
          src={rx}
          alt="Illustration of paper prescription"
        />
        <Column gap="MD">
          <Body>
            Ask your doctor to send us your prescription directly. Select the Alto location closest to you and provide
            this info to your doctor’s office as your new preferred pharmacy.
          </Body>
          <Column>
            <Body fontFamily="bold">LOCATIONS</Body>
            <InputSelect
              placeholder="Select a location"
              options={facilityOptions}
              onValueChange={selectFacility}
              value={selectedFacilityID}
              disabled={showLoadingOrError}
            />
          </Column>
          {showLocations ? renderSelectedFacility(selectedFacilityID) : null}
          {showLoadingOrError ? (
            <>
              {fetchFacilitiesLoading ? <AltoSpinningLoader /> : null}
              {fetchFacilitiesError ? (
                <InlineAlert type="error">
                  <Body fontFamily="semibold">{DEFAULT_ERROR_ALERT_TITLE}</Body>
                  <Body>
                    Please try again or{' '}
                    <RRLink
                      to="/messages"
                      onClick={closeActionSheet}
                    >
                      send us a message
                    </RRLink>{' '}
                    to get information for your nearest Alto pharmacy location.
                  </Body>
                </InlineAlert>
              ) : null}
            </>
          ) : null}
        </Column>
      </LgPadding>
    </ActionSheetV2>
  );
};
